import { useCallback, useEffect, useRef } from "react";
import { JoinTelehealthRoomResponse } from "../../../types/generated/models";
import useUrlOpener from "../hooks/useUrlOpener";
import { useAnalytics } from "../../../hooks/useAnalytics";

export type RefetchType = () => Promise<{ data?: JoinTelehealthRoomResponse }>;

export function useJoinTelehealth({
    refetch,
    isMobile,
    onBusyChange,
    roomId,
}: {
    refetch: RefetchType;
    isMobile: boolean;
    onBusyChange?: (isBusy: boolean) => void;
    roomId: string;
}) {
    const { openUrlInCurrentTab, openUrlInNewTab } = useUrlOpener();
    const { track } = useAnalytics();

    // Holds the active setTimeout id for the current poll wait.
    // Cleared on unmount so the scheduled callback never fires after teardown.
    const pollTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Sets to true on unmount to stop the loop and skip side-effects.
    const abortPollingRef = useRef(false);

    const POLL_DELAY_MS = 3000;

    useEffect(() => {
        return () => {
            abortPollingRef.current = true;
            if (pollTimeoutIdRef.current != null) {
                clearTimeout(pollTimeoutIdRef.current);
                pollTimeoutIdRef.current = null;
            }
        };
    }, []);

    // Sleep for one poll interval. The timeout is tracked for cleanup.
    const waitForNextPoll = () =>
        new Promise<void>((resolve) => {
            pollTimeoutIdRef.current = setTimeout(() => {
                pollTimeoutIdRef.current = null;
                resolve();
            }, POLL_DELAY_MS);
        });

    const pollUntilUnlocked = useCallback(async (): Promise<JoinTelehealthRoomResponse | null> => {
        onBusyChange?.(true);

        try {
            let response = await refetch();
            let data = response.data;

            while (!abortPollingRef.current && data?.isLocked) {
                await waitForNextPoll();
                if (abortPollingRef.current) break;
                response = await refetch();
                data = response.data;
            }

            return data ?? null;
        } finally {
            onBusyChange?.(false);
        }
    }, [refetch, onBusyChange]);

    const joinInApp = useCallback(async () => {
        const data = await pollUntilUnlocked();
        if (!data || abortPollingRef.current) return;

        if (data.zoomAppJoinUrl) {
            track("telehealth_join_app_clicked", {
                room_id: roomId,
                join_method: "app",
            });
            openUrlInCurrentTab(data.zoomAppJoinUrl);
        }
    }, [pollUntilUnlocked, openUrlInCurrentTab, roomId, track]);

    const joinInBrowser = useCallback(async () => {
        const data = await pollUntilUnlocked();
        if (!data || abortPollingRef.current) return;

        if (data.browserJoinUrl) {
            track("telehealth_join_browser_clicked", {
                room_id: roomId,
                is_mobile: isMobile,
                join_method: "browser",
            });
            if (isMobile) {
                openUrlInCurrentTab(data.browserJoinUrl);
            } else {
                openUrlInNewTab(data.browserJoinUrl);
            }
        }
    }, [pollUntilUnlocked, isMobile, openUrlInCurrentTab, openUrlInNewTab, roomId, track]);

    return { joinInApp, joinInBrowser };
}
